import { AbstractController, Cartridge, Nes }                                from '@/Emulator';
import { IComponent }                                                        from '@/Interface/IComponent';
import { Component, Event, EventEmitter, h, Host, JSX, Method, Prop, State } from '@stencil/core';

@Component({
    tag:      'nes-player',
    styleUrl: './nes-player.scss',
    shadow:   true,
})
export class NesPlayer implements IComponent
{
    @Event() public ready!: EventEmitter<void>;

    /**
     * Turns on the debugging panel.
     */
    @Prop() public debug: boolean = false;

    @State() private controllers: AbstractController[] = [];
    @State() private currentRom: string                = '';
    @State() private isPlaying: boolean                = false;
    @State() private games: string[]                   = [
        'dist/smb.nes',
        'dist/smb2.nes',
        'dist/smb3.nes',
        'dist/ff3.nes',
        'dist/kirby.nes',
    ];

    /**
     * A reference to the NES console.
     */
    private readonly nes: Nes = new Nes();

    public async componentWillLoad(): Promise<void>
    {
        this.nes.on('input-devices-changed', list => this.controllers = list);
    }

    /**
     * Loads a NES ROM from the given URL.
     *
     * @param {string} url - A URL pointing to a valid NES formatted ROM file.
     */
    @Method()
    public async loadFromUrl(url: string): Promise<void>
    {
        this.currentRom          = url;
        const response: Response = await fetch(url);

        if (!response.ok) {
            throw new Error(response.statusText);
        }

        return this.loadFromBuffer(await response.arrayBuffer());
    }

    /**
     * Loads a NES ROM from the given {@link ArrayBuffer}.
     *
     * @param {ArrayBuffer} buffer - The ROM data to load.
     */
    @Method()
    public async loadFromBuffer(buffer: ArrayBuffer): Promise<void>
    {
        this.nes.load(new Cartridge(buffer));
    }

    /**
     * @inheritDoc
     */
    public componentDidLoad(): void
    {
        this.ready.emit();
    }

    /**
     * @inheritDoc
     */
    public render(): JSX.Element
    {
        return (
            <Host>
                <nes-viewport nes={this.nes} onClick={() => this.onViewportClicked()}>
                    {this.renderGameSelector()}
                    {this.renderControllerSelector(1)}
                    {this.renderControllerSelector(2)}
                </nes-viewport>
                {this.debug && (
                    <aside>
                        <nes-debugger nes={this.nes}/>
                    </aside>
                )}
            </Host>
        );
    }

    private renderGameSelector(): JSX.Element
    {
        return (
            <select onInput={(e: InputEvent) => this.loadFromUrl((e.target as HTMLSelectElement).value)}
            >
                {this.games.map(game => (
                    <option
                        value={game}
                        selected={this.currentRom === game}
                    >{game.slice(game.lastIndexOf('/') + 1, game.lastIndexOf('.'))}</option>
                ))}
            </select>
        );
    }

    private renderControllerSelector(id: number): JSX.Element
    {
        return (
            <select onInput={(e: InputEvent) => {
                const controller: Nullable<AbstractController> = this.controllers.find(c => c.name === (e.target as HTMLSelectElement).value) ?? null;
                if (id === 1) {
                    this.nes.input!.setPlayer1Controller(controller);
                } else {
                    this.nes.input!.setPlayer2Controller(controller);
                }
            }}>
                <option>None</option>
                {this.controllers.map(c => (
                    <option
                        value={c.name}
                        selected={id === 1 ? this.nes.input!.isPlayer1Controller(c)
                            : this.nes.input!.isPlayer2Controller(c)}
                    >{c.name}</option>
                ))}
            </select>
        );
    }

    private onViewportClicked(): void
    {
        if (this.isPlaying) {
            return;
        }

        this.isPlaying = true;
        this.loadFromUrl('dist/smb3.nes');
    }
}
